import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class LoadingService {
    loadingSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    globalLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Número de peticiones en curso por URL (`HttpRequest.url` no incluye la query string, así
     * que varias peticiones concurrentes a la misma ruta con distintos parámetros comparten
     * clave). Antes era `Map<string, boolean>`: con 2+ peticiones concurrentes a la misma URL,
     * la primera en completarse borraba la única entrada del mapa aunque las demás siguieran en
     * curso, dejando `loadingSub` en `false` (overlay oculto) mientras aún había peticiones
     * pendientes — no se distinguía "una entrada por URL" de "N peticiones por URL". Contar
     * peticiones en vez de tratarlas como una única entrada por URL resuelve esto: `loadingSub`
     * solo pasa a `false` cuando el contador de esa URL llega a cero.
     */
    loadingMap: Map<string, number> = new Map<string, number>();

    constructor() {}

    /**
     * Sets the loadingSub property value based on the following:
     * - If loading is true, increment the in-flight request count for the provided url, set loadingSub value to true
     * - If loading is false, decrement that count (removing the entry once it reaches zero) and only when the map is
     *   empty will we set loadingSub to false
     * This pattern ensures if there are multiple requests awaiting completion, we don't set loading to false before
     * other requests have completed. At the moment, this function is only called from the @link{HttpRequestInterceptor}
     * @param loading {boolean}
     * @param url {string}
     */
    setLoading(loading: boolean, url: string): void {
        if (!url) {
            throw new Error('The request URL must be provided to the LoadingService.setLoading function');
        }

        if (loading === true) {
            this.loadingMap.set(url, (this.loadingMap.get(url) ?? 0) + 1);
            this.loadingSub.next(true);
        } else if (loading === false && this.loadingMap.has(url)) {
            const pendientes = (this.loadingMap.get(url) ?? 0) - 1;
            if (pendientes <= 0) {
                this.loadingMap.delete(url);
            } else {
                this.loadingMap.set(url, pendientes);
            }
        }
        if (this.loadingMap.size === 0) {
            this.loadingSub.next(false);
        }
    }

    /**
     * Activates the global loading state
     */
    setGlobalLoading(loading: boolean): void {
        this.globalLoading.next(loading);
    }
}
